// R-131-FIN-DECISION-F — pick 2–4 pathways and quick-edit sensitive overrides.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GitCompare, Plus, Trash2, PencilLine, Save, FolderOpen } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  ALL_PRESET_TECHNOLOGIES,
  PRESET_BY_TECHNOLOGY,
} from '../../../lib/finDecision/presets';
import { financialAssumptionsFromPathway } from '../../../lib/finDecision/pathwayAssumptionsBridge';
import {
  applyCapacityOverride,
  applyCapexOverride,
  applyCodYearOverride,
  applyLifetimeOverride,
  applyWaccOverride,
  parseCodYearInput,
  pathwayCodYear,
  pathwayCapexPerKw,
  pathwayDisplayNameWithCod,
} from '../../../lib/finDecision/pathwayQuickEdit';
import { pathwayFromFinancialAssumptions } from '../../../lib/finDecision/pathwayAssumptionsBridge';
import { recommendedPathwaysForIntent } from '../../../lib/finDecision/intentPosture';
import type { Pathway, TechnologyId } from '../../../lib/finDecision/types';
import type { FinancialAssumptions } from '../../../types/financialAssumptions';
import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type { SavedSitePathwayDocument } from '../../../types/pathwayComparison';
import { FinancialAssumptionsForm } from './FinancialAssumptionsForm';

const MIN_PATHWAYS = 2;
const MAX_PATHWAYS = 4;

const PRESET_LABELS: Record<TechnologyId, string> = {
  'gas-cc': 'Gas CC',
  'gas-sc': 'Gas SC',
  'fuel-cell-sofc': 'Fuel Cell (SOFC)',
  'fuel-cell-pafc': 'Fuel Cell (PAFC)',
  'fuel-cell-pem': 'Fuel Cell (PEM H2)',
  'fuel-cell-mcfc': 'Fuel Cell (MCFC)',
  'nuclear-smr': 'Nuclear SMR',
  'nuclear-large': 'Nuclear Large',
  'solar-bess': 'Solar + BESS',
  'wind-bess': 'Wind + BESS',
  'grid-tariff': 'Grid 2030',
  mixed: 'Hybrid bridge',
};

export interface PathwayPickerProps {
  intent: ProjectIntent;
  siteContext: SiteContext;
  pathways: Pathway[];
  onChange: (next: Pathway[]) => void;
  savedPathways?: SavedSitePathwayDocument[];
  onSavePathway?: (pathway: Pathway, label: string) => Promise<void>;
  siteId?: string;
  updatedBy: string;
  baseFinancialAssumptions?: FinancialAssumptions | null;
}

interface QuickEditFieldProps {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  suffix?: string;
}

function QuickEditField({ label, value, onChange, suffix }: QuickEditFieldProps) {
  return (
    <label className="flex flex-col gap-0.5 text-xs">
      <span className="text-text-muted">{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="text"
          className="w-full border border-border bg-surface-0 px-2 py-1 text-sm text-text-primary"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix ? <span className="text-text-muted shrink-0">{suffix}</span> : null}
      </div>
    </label>
  );
}

function clonePathway(pathway: Pathway): Pathway {
  return JSON.parse(JSON.stringify(pathway)) as Pathway;
}

export function PathwayPicker({
  intent,
  siteContext,
  pathways,
  onChange,
  savedPathways = [],
  onSavePathway,
  siteId,
  updatedBy,
  baseFinancialAssumptions,
}: PathwayPickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Pathway[]>(pathways);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailPathwayId, setDetailPathwayId] = useState<string | null>(null);

  // U4 — intent-driven preset selection. Replaces the old hardcoded
  // FEATURED_PRESETS so the picker surfaces the alternatives that match the
  // project's actual posture (nuclear interest, carbon commitment, priority,
  // timeline) and shows a rationale caption explaining why.
  const posture = useMemo(() => recommendedPathwaysForIntent(intent), [intent]);
  const featuredPresets = posture.featured;

  useEffect(() => {
    if (!open) setDraft(pathways);
  }, [open, pathways]);

  const addPreset = useCallback(
    (technology: TechnologyId) => {
      if (draft.length >= MAX_PATHWAYS) return;
      // Q7 — `mixed` has no factory since the hybrid bridge was retired.
      // `ALL_PRESET_TECHNOLOGIES` derives from the registry keys so it is not
      // offered in the list; this guards a stale caller rather than crashing.
      const factory = PRESET_BY_TECHNOLOGY[technology];
      if (!factory) return;
      const seeded = factory(intent, siteContext);
      const withSuffix = {
        ...seeded,
        id: `${seeded.id}_${Date.now()}`,
        name: pathwayDisplayNameWithCod(seeded),
      };
      setDraft((prev) => [...prev, withSuffix]);
    },
    [draft.length, intent, siteContext],
  );

  const addSaved = useCallback(
    (doc: SavedSitePathwayDocument) => {
      if (draft.length >= MAX_PATHWAYS) return;
      const copy = clonePathway(doc.pathway);
      copy.id = `${doc.pathway.id}_saved_${Date.now()}`;
      copy.name = doc.label;
      setDraft((prev) => [...prev, copy]);
    },
    [draft.length],
  );

  const removePathway = useCallback((id: string) => {
    setDraft((prev) => prev.filter((p) => p.id !== id));
    setExpandedId((cur) => (cur === id ? null : cur));
  }, []);

  const updatePathway = useCallback((id: string, next: Pathway) => {
    setDraft((prev) => prev.map((p) => (p.id === id ? next : p)));
  }, []);

  const applyQuickEdit = useCallback(
    (pathway: Pathway, field: 'capacity' | 'cod' | 'lifetime' | 'capex' | 'wacc', raw: string) => {
      const parsed = Number(raw);
      switch (field) {
        case 'capacity':
          if (Number.isFinite(parsed) && parsed > 0) {
            updatePathway(pathway.id, applyCapacityOverride(pathway, parsed));
          }
          break;
        case 'cod': {
          const year = parseCodYearInput(raw);
          if (year != null) {
            const shifted = applyCodYearOverride(pathway, year);
            updatePathway(pathway.id, {
              ...shifted,
              name: pathwayDisplayNameWithCod(shifted),
            });
          }
          break;
        }
        case 'lifetime':
          if (Number.isFinite(parsed) && parsed > 0) {
            updatePathway(pathway.id, applyLifetimeOverride(pathway, Math.round(parsed)));
          }
          break;
        case 'capex':
          if (Number.isFinite(parsed) && parsed > 0) {
            updatePathway(
              pathway.id,
              applyCapexOverride(pathway, parsed * 1_000_000),
            );
          }
          break;
        case 'wacc':
          if (Number.isFinite(parsed) && parsed > 0) {
            updatePathway(pathway.id, applyWaccOverride(pathway, parsed));
          }
          break;
        default:
          break;
      }
    },
    [updatePathway],
  );

  const handleConfirm = useCallback(() => {
    if (draft.length < MIN_PATHWAYS) return;
    onChange(draft.map(clonePathway));
    setOpen(false);
  }, [draft, onChange]);

  const detailPathway = useMemo(
    () => draft.find((p) => p.id === detailPathwayId) ?? pathways.find((p) => p.id === detailPathwayId),
    [detailPathwayId, draft, pathways],
  );

  const detailSeed = useMemo(() => {
    if (!detailPathway) return null;
    return financialAssumptionsFromPathway(
      detailPathway,
      updatedBy,
      baseFinancialAssumptions,
    );
  }, [baseFinancialAssumptions, detailPathway, updatedBy]);

  const canConfirm = draft.length >= MIN_PATHWAYS && draft.length <= MAX_PATHWAYS;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="pathway-picker-open"
        >
          <GitCompare className="w-4 h-4 mr-2" />
          Compare alternatives
          {pathways.length >= MIN_PATHWAYS ? (
            <span className="ml-2 text-text-muted">({pathways.length} selected)</span>
          ) : null}
        </Button>
        {pathways.length > 0 && pathways.length < MIN_PATHWAYS ? (
          <span className="text-xs text-warning">
            Select at least {MIN_PATHWAYS} pathways to compare.
          </span>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compare power pathways</DialogTitle>
            <DialogDescription>
              Pick {MIN_PATHWAYS}–{MAX_PATHWAYS} alternatives — preset templates or saved site pathways.
              Quick-edit the five most sensitive drivers per row; open detailed edit for full assumptions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                Suggested for your project intent
              </div>
              <div className="flex flex-wrap gap-3" data-testid="pathway-picker-featured">
                {featuredPresets.map((tech) => (
                  <div key={tech} className="flex flex-col gap-1 max-w-[180px]">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={draft.length >= MAX_PATHWAYS}
                      onClick={() => addPreset(tech)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {PRESET_LABELS[tech]}
                    </Button>
                    {posture.rationale[tech] ? (
                      <span
                        className="text-[10px] leading-snug text-text-muted"
                        data-testid={`pathway-picker-rationale-${tech}`}
                      >
                        {posture.rationale[tech]}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
              <details className="mt-2">
                <summary className="text-xs text-text-secondary cursor-pointer">All presets</summary>
                <div className="mt-2 flex flex-wrap gap-2">
                  {ALL_PRESET_TECHNOLOGIES.filter((t) => !featuredPresets.includes(t)).map((tech) => (
                    <Button
                      key={tech}
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={draft.length >= MAX_PATHWAYS}
                      onClick={() => addPreset(tech)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {PRESET_LABELS[tech]}
                    </Button>
                  ))}
                </div>
              </details>
            </div>

            {savedPathways.length > 0 ? (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                  Saved on this site
                </div>
                <div className="flex flex-wrap gap-2">
                  {savedPathways.map((doc) => (
                    <Button
                      key={doc.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={draft.length >= MAX_PATHWAYS}
                      onClick={() => addSaved(doc)}
                    >
                      <FolderOpen className="w-3 h-3 mr-1" />
                      {doc.label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-3">
              {draft.map((pathway) => {
                const expanded = expandedId === pathway.id;
                const capexM = pathway.capex.baseUsd / 1_000_000;
                return (
                  <div
                    key={pathway.id}
                    className="border border-border bg-surface-1 p-3 rounded-md"
                    data-testid={`pathway-picker-row-${pathway.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-text-primary">{pathway.name}</div>
                        <div className="text-xs text-text-muted">
                          {PRESET_LABELS[pathway.technology]} · {Number.isFinite(pathway.capacityMW) ? pathway.capacityMW.toFixed(1) : pathway.capacityMW} MW · COD {pathwayCodYear(pathway)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedId(expanded ? null : pathway.id)}
                        >
                          Quick edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setDetailPathwayId(pathway.id)}
                        >
                          <PencilLine className="w-3 h-3 mr-1" />
                          Detailed
                        </Button>
                        {onSavePathway ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => void onSavePathway(pathway, pathway.name)}
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removePathway(pathway.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {expanded ? (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
                        <QuickEditField
                          label="Capacity"
                          value={pathway.capacityMW}
                          suffix="MW"
                          onChange={(v) => applyQuickEdit(pathway, 'capacity', v)}
                        />
                        <QuickEditField
                          label="COD year"
                          value={pathwayCodYear(pathway)}
                          onChange={(v) => applyQuickEdit(pathway, 'cod', v)}
                        />
                        <QuickEditField
                          label="Lifetime"
                          value={pathway.lifetime.designLifeYears}
                          suffix="yr"
                          onChange={(v) => applyQuickEdit(pathway, 'lifetime', v)}
                        />
                        <QuickEditField
                          label="CAPEX"
                          value={capexM.toFixed(0)}
                          suffix="M$"
                          onChange={(v) => applyQuickEdit(pathway, 'capex', v)}
                        />
                        <QuickEditField
                          label="WACC"
                          value={pathway.risk.baseWaccPct}
                          suffix="%"
                          onChange={(v) => applyQuickEdit(pathway, 'wacc', v)}
                        />
                        <div className="col-span-full text-[10px] text-text-muted">
                          Implied ${pathwayCapexPerKw(pathway).toFixed(0)}/kW · risk premium synced on save
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {draft.length === 0 ? (
                <div className="text-sm text-text-secondary border border-dashed border-border p-4 text-center">
                  Add preset templates or saved pathways to begin.
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={!canConfirm} onClick={handleConfirm} data-testid="pathway-picker-confirm">
              Compare {draft.length} pathways
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detailPathway && detailSeed ? (
        <Dialog open={detailPathwayId != null} onOpenChange={(v) => !v && setDetailPathwayId(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detailed assumptions — {detailPathway.name}</DialogTitle>
              <DialogDescription>
                Scoped financial assumptions for this pathway. Changes apply to the comparison draft only.
              </DialogDescription>
            </DialogHeader>
            <FinancialAssumptionsForm
              initial={detailSeed}
              siteId={siteId}
              // C12 — this dialog's own description promises "the comparison
              // draft only". Without this the form rendered a "Pull from BOM +
              // Intent" button that persisted the SITE's financial model.
              draftOnly
              projectIntent={intent}
              updatedBy={updatedBy}
              onSave={(next) => {
                // C12 — ONE named inverse of the seeding bridge
                // (`pathwayFromFinancialAssumptions`), so the two directions can
                // be round-trip tested. This used to be a hand-rolled literal
                // that mapped ELEVEN of the ~18 editable fields: a capacity
                // factor, in-service date, construction duration,
                // decommissioning cost or working-capital figure typed here was
                // accepted, saved without complaint, and discarded.
                const merged = pathwayFromFinancialAssumptions(detailPathway, next);
                updatePathway(detailPathway.id, merged);
                setDetailPathwayId(null);
              }}
              onCancel={() => setDetailPathwayId(null)}
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
